/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import {
  Card,
  CardContent,
  Control,
  ControlHelper,
  Input,
  Label,
  Button,
  PageLoader,
  ControlError,
  Toggle,
  EmptyState
} from "@forge/common";
import React, { useEffect, useState } from "react";
import {
  BASIC_INFO_MAX_CHARS,
  BASIC_INFO_MIN_CHARS,
  LOCALE_CONST
} from "../../utils/constants";
import "./styles.scss";
import { useGetDeviceDetailsQuery, useReRegistrationKeyMutation } from "../../services/api";
import Toast from "../../reusablecomponents/toast";
import { setDeviceListUpdated, setHeaderDetails } from "../../actions";
import { useDispatch } from "react-redux";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { ConfigurationVariables } from "../../config";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { gatewayErrorStatusMessages } from "../../utils/utility";

const ReRegistration = () => {
  const TIMEOUT_DURATION = 3000; // 3 seconds
  const REGISTRATION_KEY_LENGTH = 6;

  const { pathname: locationId } = useLocation();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [reqObject, setReqObject] = useState<any>({
    name: "",
    registrationKey: "",
    migration: false,
    systemGUID: ""
  });
  const [deviceId, setDeviceId] = React.useState("");
  const [reRegistrationKey, { isLoading }] = useReRegistrationKeyMutation(); // Mutation hook
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<
    "error" | "success" | "warning" | "progress"
  >("success");
  const [toastContent, setToastContent] = useState("");
  const { navigateToLandingPage } = useNavigateToLandingPage();
  const [errorState, setErrorState] = useState({
    showNameError: false,
    showRegistrationKeyError: true,
    showValidRegistrationKeyError: false,
    showSystemGUIDError: false,
    showValidSystemGUIDError: false,
    showNameTypeError: false
  });

  const [showSystemGUIDFiled, setShowSystemGUIDFiled] = useState(false);
  // To update the error state
  const setError = (field: string, value: boolean) => {
    setErrorState((prevState) => ({
      ...prevState,
      [field]: value
    }));
  };

  // Special characters validation
  const specialCharPattern = /[!@#$%^&*(),.?":{}|<>\\[\];=+_]/g;
  const scriptTagPattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const validateSpecialChar = (value) => {
    return !specialCharPattern.test(value) && !scriptTagPattern.test(value);
  };

  useEffect(() => {
    dispatch(setHeaderDetails({
      headerDetails: {
        status: '',
        type: 'reregistration',
        title: t(LOCALE_CONST.editGateway) || ''
      }
    }));
  }, []);

  useEffect(() => {
    const splitLocIDs = locationId?.split?.("/");
    const gatewayId = splitLocIDs?.[splitLocIDs?.length - 1];
    setDeviceId(gatewayId || "");
  }, [locationId]);

  const {
    data: deviceRec,
    isError,
    error: deviceDetailError,
    isLoading: isDeviceDetailsLoading,
    refetch: deviceDetailsRefetch
  } = useGetDeviceDetailsQuery({ deviceId }, { skip: !deviceId });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    if (type === "name") {
      setError("showNameError", false);
      setError("showNameTypeError", false);
      setReqObject({ ...reqObject, name: deviceRec?.name || "" });
      if (/ {2}|%/.test(e.target.value)) {
        setError("showNameTypeError", true);
      }
      if (reqObject.migration) {
        setReqObject({ ...reqObject, name: e.target.value });
      }
    } else if (type === "registrationKey") {
      setError("showRegistrationKeyError", false);
      setError("showValidRegistrationKeyError", false);
      setReqObject({ ...reqObject, registrationKey: e.target.value });
      const isValidRegKey = (e.target.value.length !== REGISTRATION_KEY_LENGTH) || (e.target.value !== e.target.value.toUpperCase());
      if ((isValidRegKey || !validateSpecialChar(e.target.value)) && e.target.value !== "") setError("showValidRegistrationKeyError", true);
    } else if (type === "migration") {
      setError("showSystemGUIDError", false);
      if (e.target.checked) {
        setReqObject({ ...reqObject, migration: e.target.checked });
      } else {
        setReqObject({ ...reqObject, migration: e.target.checked, systemGUID: "" });
      }
    } else if (type === "systemGUID") {
      setError("showSystemGUIDError", false);
      setError("showValidSystemGUIDError", false);
      setReqObject({ ...reqObject, systemGUID: e.target.value });
      if (!validateSpecialChar(e.target.value)) setError("showValidSystemGUIDError", true);
    }
  };

  const handleCancel = () => {
    navigateToLandingPage();
  };

  const isSaveEnabled = () => {
    if(reqObject.registrationKey === "") {
      return true;
    } else if(showSystemGUIDFiled && reqObject.systemGUID === "") {
      return true;
    } else {
      return false;
    }
  };

  const handleCreate = async () => {
    if (reqObject.name === "" && reqObject.registrationKey === "" && showSystemGUIDFiled && reqObject.systemGUID === "") {
      setError("showNameError", true);
      setError("showRegistrationKeyError", true);
      setError("showSystemGUIDError", true);
      return;
    } else if (reqObject.name === "" && reqObject.registrationKey === "") {
      setError("showNameError", true);
      setError("showRegistrationKeyError", true);
      return;
    } else if (reqObject.name === "") {
      setError("showNameError", true);
      return;
    } else if (reqObject.registrationKey === "") {
      setError("showRegistrationKeyError", true);
      return;
    } else if ((reqObject.registrationKey.length !== REGISTRATION_KEY_LENGTH) || (reqObject.registrationKey !== reqObject.registrationKey.toUpperCase())) {
      setError("showValidRegistrationKeyError", true);
      return;
    } else if (!validateSpecialChar(reqObject.registrationKey)) {
      setError("showValidRegistrationKeyError", true);
      return;
    } else if (showSystemGUIDFiled && reqObject.systemGUID === "") {
      setError("showSystemGUIDError", true);
      return;
    } else if (showSystemGUIDFiled && !validateSpecialChar(reqObject.systemGUID)) {
      setError("showValidSystemGUIDError", true);
      return;
    } else if (reqObject.name.length < BASIC_INFO_MIN_CHARS || reqObject.name.length > BASIC_INFO_MAX_CHARS) {
      showErrorToast(t(LOCALE_CONST.gwNameValidation));
      return;
    }

    try {
      const result = await reRegistrationKey({
        deviceId: deviceId,
        registration_key: reqObject.registrationKey
      })
       .unwrap();
      if (result) {
        showSuccessToast(t(LOCALE_CONST.gwNameSuccessReRegistration, { gatewayName: reqObject.name.trim() }));
        dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
        setReqObject({
          name: "",
          registrationKey: "",
          migration: false,
          systemGUID: ""
        });
      }
    } catch (error) {
      handleApiError(error); // Handle specific error cases
    }
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        if (toastType === "success") {
          navigateToLandingPage();
        }
      }, TIMEOUT_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastType]);

  // Helper functions
  const showSuccessToast = (message: any) => {
    setToastType("success");
    setToastContent(message);
    setShowToast(true);
  };

  const showErrorToast = (message: string) => {
    setToastType("error");
    setToastContent(message);
    setShowToast(true);
  };

  const handleApiError = (error: any) => {
    const errorMessages: { [key: number]: string } = {
      400: t(LOCALE_CONST.notSaveGWDetails),
      401: t(LOCALE_CONST.unAuthFails),
      403: t(LOCALE_CONST.AuthForbidFails),
      404: t(LOCALE_CONST.projectIDNotFound),
      409: t(LOCALE_CONST.gwNameAlreadyExists, { gatewayName: reqObject.name }),
      500: t(LOCALE_CONST.internalServerError),
      502: t(LOCALE_CONST.badGatewayTryAgainLater),
      503: t(LOCALE_CONST.serviceUnavailTryAgainLater),
      504: t(LOCALE_CONST.gwTimeoutRequestTryAgainLater)
    };

    if(error?.data?.message?.match?.(/Registration/)) {
      errorMessages[404] = t(LOCALE_CONST["404RegNotFound"]);
      errorMessages[410] = t(LOCALE_CONST.regKeyExpired);
    }

    showErrorToast(errorMessages[error?.status] || t(LOCALE_CONST.somethingWentWrong));
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showToast]);

  useEffect(() => {
    if (!reqObject.migration) {
      setReqObject({ ...reqObject, name: deviceRec?.name || "" });
    }

  }, [deviceRec, reqObject.migration]);

  const handelMigrationToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowSystemGUIDFiled(!showSystemGUIDFiled);
    handleInputChange(e, "migration");
  };

  const convertFn = (value: string) => t(value);

  if (isError) {
    return (
      <EmptyState variant="4xx"
        title={deviceDetailError && "status" in deviceDetailError && typeof deviceDetailError.status === "number"
          ? gatewayErrorStatusMessages(convertFn, `${t(LOCALE_CONST.somethingWentWrong)}...`, deviceDetailError.status, { 404: t(LOCALE_CONST.clientError_404) })
          : `${t(LOCALE_CONST.somethingWentWrong)}...`}
        description={`${t(LOCALE_CONST.tryReloading)}. (${t(LOCALE_CONST.errCode, { errorCode: "status" in deviceDetailError ? deviceDetailError.status : t(LOCALE_CONST.error) })})`}
      >
        <Button className="mx-auto block" onClick={deviceDetailsRefetch}>{t(LOCALE_CONST.reloadPage)}</Button>
      </EmptyState>
    );
  }
  else {
    return (
      <>
        <PageLoader isLoading={isDeviceDetailsLoading || isLoading || false} />

        <div>
          <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl pl-3">
            <CardContent top="none">
              <span className="basic-info-text pt-5 block">
                {t(LOCALE_CONST.basicInfo)}
              </span>
              <Control className="mb-4 w-[34rem] mt-2" spacingAbove spacingBelow>
                <Label className="font-normal" required requiredText="">
                  {t(LOCALE_CONST.name)}
                </Label>
                <Input
                  id="name"
                  value={reqObject.migration ? reqObject.name : deviceRec?.name}
                  onChange={(e) => handleInputChange(e, "name")}
                  className={
                    errorState.showNameError || errorState.showNameTypeError
                      ? "error-input-text"
                      : "normal-input"
                  }
                  error={errorState.showNameError || errorState.showNameTypeError}
                  required
                  data-testid="test-gateway-name"
                  maxLength={50}
                  disabled={reqObject.migration ? false : true}
                />
                {errorState.showNameTypeError && (
                  <ControlError showIcon>{t(LOCALE_CONST.invalidCharacter)}</ControlError>
                )}
                {errorState.showNameError && (
                  <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                )}
                <ControlHelper className="helper-text">
                  {t(LOCALE_CONST.gatewayNameValidText)}
                </ControlHelper>
              </Control>
              <Control className="mb-4 w-[34rem] mt-2">
                <Label className="font-normal" required requiredText="">
                  {t(LOCALE_CONST.enterRegKey)}
                </Label>
                <Input
                  id="registrationKey"
                  onChange={(e) => handleInputChange(e, "registrationKey")}
                  className={
                    errorState.showRegistrationKeyError
                      ? "error-input-text"
                      : "normal-input"
                  }
                  error={errorState.showRegistrationKeyError}
                  data-testid="test-registration-key"
                  value={reqObject.registrationKey}
                  maxLength={6}
                />
                {errorState.showRegistrationKeyError && (
                  <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                )}
                {errorState.showValidRegistrationKeyError && (
                  <ControlError showIcon>{t(LOCALE_CONST.regKeyValidation)}</ControlError>
                )}
                <ControlHelper className="helper-text">
                  {t(LOCALE_CONST.manualKeyGenerText)},
                  <a className="text-blue-500" href={`mailto:${ConfigurationVariables.HONEYWELL_SUPPORT_EMAILID}`}> {t(LOCALE_CONST.contact)} </a>
                  {t(LOCALE_CONST.systemIntegrator)}
                </ControlHelper>
              </Control>
              <div
                className={`w-[34rem] mt-2 p-0 ${showSystemGUIDFiled ? "mb-4" : ""
                  }`}
              >
                <Label className="font-normal" optional optionalText={`- ${t(LOCALE_CONST.optional)}`}>
                  {t(LOCALE_CONST.existingGateway)}
                </Label>
                <p className="helper-text py-2 text-sm">
                  {t(LOCALE_CONST.migrateGWConfirmation)}
                </p>
                <Toggle
                  id="migration"
                  onChange={handelMigrationToggle}
                  data-testid="test-migration"
                  disabled={true}
                />
              </div>
              {showSystemGUIDFiled && (
                <div className="w-[34rem] mt-2 pb-0">
                  <Label className="font-normal pb-2" required requiredText="">
                    {t(LOCALE_CONST.enterSystemGUID)}
                  </Label>
                  <Input
                    id="systemGUID"
                    className={
                      errorState.showSystemGUIDError
                        ? "error-input-text"
                        : "normal-input"
                    }
                    onChange={(e) => handleInputChange(e, "systemGUID")}
                    error={errorState.showSystemGUIDError}
                    data-testid="test-system-guid"
                    value={reqObject.systemGUID}
                    maxLength={50}
                  />
                  {errorState.showSystemGUIDError && (
                    <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                  )}
                  {errorState.showValidSystemGUIDError && (
                    <ControlError showIcon>{t(LOCALE_CONST.enterValidSystemGUID)}</ControlError>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="re-registration-button-Container flex space-x-4 mt-6">
          <Button variant="secondary" onClick={handleCancel}>
            {t(LOCALE_CONST.cancel)}
          </Button>
          <Button variant="primary" disabled={isSaveEnabled()} onClick={handleCreate}>
            {t(LOCALE_CONST.save)}
          </Button>
        </div>

        {showToast && (
          <div className="toast-container">
            <Toast
              id="delete-toast"
              showClose={false}
              showDismiss={false}
              title={toastContent}
              type={toastType}
              lifetime={3000}
              subtitle={""}
              content={""}
            />
          </div>
        )}
      </>
    );
  }
};

export default ReRegistration;
